import _ from "lodash";
import React, { Component } from "react";
import { useSelector } from "react-redux";
import uuid from "react-uuid";
import { Modal, Button, Radio, Select, Input, Tooltip } from "../../../../GeneralComponents/ui";
import { FunctionOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { ReactComponent as WhatIfIcon } from "../../../../../images/whatif_dashboardvariable.svg";
import i18n from "../../../../../Utils/i18next";
import "./whatIf.css";
import CreateUpdate from "./CreateUpdate";
import { showNotificationWithIcon } from "../../../../../Utils/Notification";
import List from "./List";
import WhatIfScenarios from "./WhatIfScenarios";
import { getIcon, getPopupContainer, deepCopy } from "../../../../../Utils/Global";
import ScenarioList from "./ScenarioList";
import DashboardVariableTable from "../../../../GeneralComponents/DashboardVariable/DashboardVariableTable";
import DashboardVariableCreateUpdate from "./DashboardVariableCreateUpdate";

const { Option } = Select;

export const TYPE_INPUT = "INPUT";
export const TYPE_SLIDER = "SLIDER";
export const MODE_WHAT_IF = "WHAT_IF";
export const MODE_VARIABLES = "VARIABLES";
export const SUBVIEW_LIST = "LIST";

/**
 * What if base component
 * Includes what if parameters, scenarios, current scenario
 * When parameter changed, refreshs all plugins.
 */
export default class WhatIf extends Component {
  constructor(props) {
    super(props);

    const { parameters, scenarios, whatIfId, changed } = this.ensureIds(
      this.props.whatIf?.parameters,
      this.props.whatIf?.scenarios,
      this.props.whatIf?.id
    );

    this.state = {
      visibility: false,
      parameters: parameters,
      scenarios: scenarios,
      selectedParameter: undefined,
      currentScenario: undefined,
      activeMode: MODE_VARIABLES,
      subView: SUBVIEW_LIST,
      showSaveScenarioModal: false,
      tempScenarioName: "",
      isEditingScenario: false,
      showDeleteConfirmModal: false,
      showParameterModal: false,
      showVariableModal: false,
      selectedVariable: undefined,
      showVariableDeleteConfirmModal: false,
      variableToDelete: undefined,
      showVariableDisconnectConfirmModal: false,
      variableToSavePendingDisconnect: undefined,
      whatIfId: whatIfId,
    };

    if (changed && this.props.changeWhatIf) {
      this.props.changeWhatIf(parameters, scenarios, whatIfId);
    }
  }

  ensureIds = (parameters = [], scenarios = [], whatIfId = null) => {
    let changed = false;
    let nextWhatIfId = whatIfId;

    if (!nextWhatIfId) {
      nextWhatIfId = uuid();
      changed = true;
    }

    const nextParameters = (parameters || []).map((p) => {
      if (!p.id) {
        changed = true;
        return { ...p, id: uuid() };
      }
      return p;
    });

    const nextScenarios = (scenarios || []).map((s) => {
      let scenarioChanged = false;
      let newS = { ...s };

      if (!newS.id) {
        newS.id = uuid();
        changed = true;
        scenarioChanged = true;
      }

      const nextScenarioParametersList = (newS.scenarioParametersList || []).map(
        (sp) => {
          if (!sp.id) {
            changed = true;
            scenarioChanged = true;
            return { ...sp, id: uuid() };
          }
          return sp;
        }
      );

      if (scenarioChanged) {
        newS.scenarioParametersList = nextScenarioParametersList;
      }

      return newS;
    });

    return {
      parameters: nextParameters,
      scenarios: nextScenarios,
      whatIfId: nextWhatIfId,
      changed,
    };
  };

  blurTriggerButton = () => {
    const button = document.getElementById("whatIfButton");

    if (!button) {
      return;
    }

    const activeElement = document.activeElement;

    if (
      activeElement &&
      (activeElement === button || button.contains(activeElement))
    ) {
      button.blur();
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.whatIf !== this.props.whatIf) {
      const { parameters, scenarios, whatIfId, changed } = this.ensureIds(
        this.props.whatIf?.parameters,
        this.props.whatIf?.scenarios,
        this.props.whatIf?.id
      );

      const newState = {
        ...this.state,
        whatIfActiveTabKey: parameters.length > 0 ? "1" : "2",
        parameters: [...parameters],
        scenarios: [...scenarios],
        whatIfId: whatIfId,
      };

      this.setState(newState);

      if (changed && this.props.changeWhatIf) {
        this.props.changeWhatIf(parameters, scenarios, whatIfId);
      }

      this.props.setWhatIfReducer(
        newState.parameters,
        newState.scenarios,
        newState.currentScenario
      );
    }

    if (prevProps.dashboardVariables !== this.props.dashboardVariables) {
      this.forceUpdate();
    }
  }

  /** Changes visibility on click what if button.*/
  whatIfOnClick = (status) => {
    this.setState(
      {
        ...this.state,
        visibility: status,
      },
      () => {
        if (status === false) {
          this.blurTriggerButton();
        }
      },
    );
  };

  /** Handle mode switch between WhatIf and Variables */
  handleModeChange = (e) => {
    const value = e && e.target ? e.target.value : e;
    this.setState({
      ...this.state,
      activeMode: value,
      showParameterModal: false,
    });
  };

  handleAddParameter = () => {
    this.setState({
      selectedParameter: undefined,
      showParameterModal: true,
    });
  };

  handleCancelEdit = () => {
    this.setState({
      ...this.state,
      selectedParameter: undefined,
      showParameterModal: false,
    });
  };

  // For change active tab (list - add new)
  changeActiveTab = (key) => {
    const newState = { ...this.state, whatIfActiveTabKey: key };

    if (key === "1") {
      newState.selectedParameter = undefined;
      newState.showParameterModal = false;
    }
    this.setState(newState);
  };

  /** Opens variable creation modal */
  handleAddVariable = () => {
    this.setState({
      selectedVariable: undefined,
      showVariableModal: true
    });
  };

  /** Opens variable editing modal */
  handleEditVariable = (variable) => {
    this.setState({
      selectedVariable: { ...variable },
      showVariableModal: true
    });
  };

  /** Closes variable modal */
  handleCancelVariableEdit = () => {
    this.setState({
      selectedVariable: undefined,
      showVariableModal: false
    });
  };

  /** Saves variable (create or update) */
  handleSaveVariable = (variable) => {
    let processedVariable = { ...variable };
    if (processedVariable.inputType === "range" && processedVariable.source?.pluginId) {
      const assignedPlugin = (this.props.plugins || []).find(p => p.id === processedVariable.source.pluginId);
      if (assignedPlugin) {
        const isRangeSupported = assignedPlugin.key === "number-filter" || assignedPlugin.key === "date-filter";
        if (!isRangeSupported) {
          this.setState({
            showVariableDisconnectConfirmModal: true,
            variableToSavePendingDisconnect: processedVariable
          });
          return;
        }
      }
    }

    this.executeSaveVariable(processedVariable);
  };

  executeSaveVariable = (processedVariable) => {
    const dashboardVariables = this.props.dashboardVariables || [];
    const index = dashboardVariables.findIndex((v) => v.id === processedVariable.id);
    const oldVariable = dashboardVariables.find((v) => v.id === processedVariable.id);

    let newVariables;

    if (index !== -1) {
      newVariables = dashboardVariables.map((v, i) => i === index ? { ...processedVariable } : v);
      showNotificationWithIcon(i18n.t("Success"), i18n.t("DashboardVariables.SuccessfullyUpdated"), "success");
    } else {
      newVariables = [...dashboardVariables, processedVariable];
      showNotificationWithIcon(i18n.t("Success"), i18n.t("DashboardVariables.SuccessfullyAdded"), "success");
    }

    if (this.props.changeDashboardVariables) {
      this.props.changeDashboardVariables(newVariables);

      const isValueChanged = !_.isEqual(processedVariable.defaultValue, this.props.activeDashboardVariableValues?.get(processedVariable.id));
      const isDataTypeChanged = processedVariable.dataType !== oldVariable?.dataType;
      const shouldResetValue = isDataTypeChanged || isValueChanged;

      if (shouldResetValue && this.props.resetDashboardVariableValue) {
        this.props.resetDashboardVariableValue(processedVariable.id);
      }
    }

    this.handleCancelVariableEdit();
  };

  handleConfirmVariableDisconnect = () => {
    const variable = this.state.variableToSavePendingDisconnect;
    if (variable) {
      delete variable.source;
      this.executeSaveVariable(variable);
    }
    this.setState({
      showVariableDisconnectConfirmModal: false,
      variableToSavePendingDisconnect: undefined
    });
  };

  handleCancelVariableDisconnect = () => {
    this.setState({
      showVariableDisconnectConfirmModal: false,
      variableToSavePendingDisconnect: undefined
    });
  };

  /** Opens variable delete confirm modal */
  handleOpenVariableDeleteConfirm = (variable) => {
    this.setState({
      showVariableDeleteConfirmModal: true,
      variableToDelete: variable
    });
  };

  /** Closes variable delete confirm modal */
  handleCloseVariableDeleteConfirm = () => {
    this.setState({
      showVariableDeleteConfirmModal: false,
      variableToDelete: undefined,
      selectedVariable: undefined,
      showVariableModal: false
    });
  };

  /** Confirms variable deletion */
  handleConfirmVariableDelete = () => {
    if (this.state.variableToDelete) {
      this.deleteDashboardVariable(this.state.variableToDelete);
      this.handleCloseVariableDeleteConfirm();
      showNotificationWithIcon(i18n.t("Success"), i18n.t("DashboardVariables.SuccessfullyDeleted"), "success");
    }
  };

  /** Adds a dashboard variable and notifies parent/redux */
  addDashboardVariable = (variable) => {
    const dashboardVariables = this.props.dashboardVariables || [];
    const newVariables = [...dashboardVariables, variable];
    if (this.props.changeDashboardVariables) {
      this.props.changeDashboardVariables(newVariables);
    }
  };

  /** Updates an existing dashboard variable */
  updateDashboardVariable = (variable) => {
    const dashboardVariables = this.props.dashboardVariables || [];
    const index = dashboardVariables.findIndex((v) => v.id === variable.id);
    let newVariables;
    if (index !== -1) {
      newVariables = dashboardVariables.map((v, i) => i === index ? { ...variable } : v);
    } else {
      newVariables = [...dashboardVariables, variable];
    }
    if (this.props.changeDashboardVariables) {
      this.props.changeDashboardVariables(newVariables);
    }
  };

  /** Deletes a dashboard variable */
  deleteDashboardVariable = (variable) => {
    const dashboardVariables = this.props.dashboardVariables || [];
    const newVariables = dashboardVariables.filter((v) => v.id !== variable.id);
    if (this.props.changeDashboardVariables) {
      this.props.changeDashboardVariables(newVariables);
    }
  };

  //Handles edited or created parameter and adds parameters list.
  applyWhatIfParameter = (parameter) => {
    let parameters = [];
    let hasParameter = false;

    this.state.parameters.map((w) => {
      if (w.id === parameter.id) {
        parameters.push(parameter);
        hasParameter = true;
      } else {
        parameters.push(w);
      }
    });

    if (hasParameter === false) {
      parameters.push(parameter);
    }

    this.setState(
      {
        ...this.state,
        parameters: [...parameters],
        whatIfActiveTabKey: "1",
        showParameterModal: false,
      },
      () => this.props.changeWhatIf(parameters, this.state.scenarios, this.state.whatIfId),
    );

    let successMessage = i18n.t("Dashboard.Data.WhatIf.SuccessfullyAdded");

    if (hasParameter) {
      successMessage = i18n.t("Dashboard.Data.WhatIf.SuccessfullyUpdated");
    }

    showNotificationWithIcon(i18n.t("Success"), successMessage, "success");

    this.props.setWhatIfReducer(
      parameters,
      this.state.scenarios,
      this.state.currentScenario,
    );
  };

  /** If any parameter value changes, update current value for this parameters */
  updateCurrentValue = (id, currentValue) => {
    let parameters = this.state.parameters.map((w) => {
      if (w.id === id) {
        return { ...w, currentValue: currentValue };
      }
      return w;
    });

    this.setState({
      ...this.state,
      currentScenario: undefined,
      parameters: parameters,
    });

    if (this.props.changeWhatIf) {
      this.props.changeWhatIf(parameters, this.state.scenarios, this.state.whatIfId);
    }

    this.props.setWhatIfReducer(parameters, this.state.scenarios);
    this.props.refresh(null, true);
  };

  /** Remove parameter in parameters list */
  removeParameter = (id) => {
    let parameters = [...this.state.parameters];
    let newState = { ...this.state };

    parameters = parameters.filter((p) => p.id !== id);
    newState.parameters = [...parameters];

    if (parameters.length === 0) {
      newState.scenarios = [];
      newState.selectedParameter = undefined;
    }

    this.setState(newState, () =>
      this.props.changeWhatIf(parameters, newState.scenarios, this.state.whatIfId),
    );
    this.props.setWhatIfReducer(
      parameters,
      newState.scenarios,
      newState.currentScenario,
    );
  };

  /** Handle parameter by id for edition, and change tab to edit  */
  setSelectedParameters = (id) => {
    let parameter = this.state.parameters.filter((w) => w.id === id)[0];

    this.setState({
      ...this.state,
      selectedParameter: { ...parameter },
      whatIfActiveTabKey: "2",
      showParameterModal: true,
    });
  };

  /** Close edit screen after cancel */
  cancelEditParameter = () => {
    this.setState({
      ...this.state,
      selectedParameter: undefined,
      whatIfActiveTabKey: "1",
      showParameterModal: false,
    });
  };

  //Add new scenario in scenario list for what if object.
  addScenario = (scenario) => {
    let scenarios = [...this.state.scenarios];

    scenarios.push(scenario);

    this.setState(
      {
        ...this.state,
        currentScenario: scenario,
        scenarios: scenarios,
      },
      () => this.props.changeWhatIf(this.state.parameters, scenarios, this.state.whatIfId),
    );

    this.props.setWhatIfReducer(this.state.parameters, scenarios, scenario);
  };

  //Remove scenario in scenario list for what if object.
  removeScenario = (id) => {
    let scenarios = [...this.state.scenarios];
    let currentScenario = { ...this.state.currentScenario };

    scenarios = scenarios.filter((s) => s.id !== id);

    if (currentScenario && currentScenario.id === id) {
      currentScenario = undefined;
    }

    this.setState(
      {
        ...this.state,
        currentScenario: currentScenario,
        scenarios: scenarios,
      },
      () => this.props.changeWhatIf(this.state.parameters, scenarios, this.state.whatIfId),
    );

    this.props.setWhatIfReducer(
      this.state.parameters,
      scenarios,
      currentScenario,
    );
  };

  /** If clicked any scenario, updates all parameters value. */
  updateCurrentValuesByScenario = (scenarioId) => {
    const scenario = this.state.scenarios.find(s => s.id === scenarioId);

    if (!scenario) {
      this.setState({ currentScenario: undefined });
      return;
    }

    let parameters = [];
    let parameterValues = new Map();

    scenario?.scenarioParametersList?.map((param) => {
      parameterValues.set(param.id, param.currentValue);
    });

    for (let i = 0; i < this.state.parameters.length; i++) {
      let value = parameterValues.get(this.state.parameters[i].id);
      parameters[i] = { ...this.state.parameters[i] };

      if (value) {
        parameters[i].currentValue = value;
      }
    }

    this.setState({
      ...this.state,
      currentScenario: scenario,
      parameters: parameters,
    });

    if (this.props.changeWhatIf) {
      this.props.changeWhatIf(parameters, this.state.scenarios, this.state.whatIfId);
    }

    this.props.setWhatIfReducer(parameters, this.state.scenarios, scenario);
    this.props.refresh(null, true);
  };

  /** Updates current scenario's parameters with current values */
  handleUpdateScenario = () => {
    const { currentScenario, scenarios, parameters } = this.state;
    if (!currentScenario) return;

    let updatedScenarios = deepCopy(scenarios);
    let index = updatedScenarios.findIndex(s => s.id === currentScenario.id);

    if (index !== -1) {
      updatedScenarios[index].scenarioParametersList = parameters.map((param) => ({
        id: param.id,
        currentValue: param.currentValue,
      }));

      this.setState({
        scenarios: updatedScenarios,
        currentScenario: updatedScenarios[index]
      }, () => {
        this.props.changeWhatIf(this.state.parameters, updatedScenarios, this.state.whatIfId);
        this.props.setWhatIfReducer(this.state.parameters, updatedScenarios, updatedScenarios[index]);
      });
      showNotificationWithIcon(i18n.t("Success"), i18n.t("Dashboard.Data.WhatIf.SuccessfullyUpdated"), "success");
    }
  };

  /** Opens save scenario naming modal */
  openSaveScenarioModal = () => {
    this.setState({
      showSaveScenarioModal: true,
      tempScenarioName: "",
      isEditingScenario: false,
    });
  };

  /** Opens edit scenario naming modal */
  openEditScenarioModal = () => {
    if (this.state.currentScenario) {
      this.setState({
        showSaveScenarioModal: true,
        tempScenarioName: this.state.currentScenario.name,
        isEditingScenario: true,
      });
    }
  };

  /** Closes save scenario naming modal */
  closeSaveScenarioModal = () => {
    this.setState({
      showSaveScenarioModal: false,
      tempScenarioName: "",
      isEditingScenario: false,
    });
  };

  /** Handles actual scenario creation and saving (or updating name) */
  handleSaveScenario = () => {
    const { tempScenarioName, parameters, isEditingScenario, currentScenario, scenarios } = this.state;

    if (!tempScenarioName || tempScenarioName.trim() === "") {
      showNotificationWithIcon(
        i18n.t("Error"),
        i18n.t("Dashboard.Data.WhatIf.ScenarioNameError"),
        "error"
      );
      return;
    }

    if (isEditingScenario && currentScenario) {
      // Update existing scenario name
      let updatedScenarios = deepCopy(scenarios);
      let index = updatedScenarios.findIndex(s => s.id === currentScenario.id);
      if (index !== -1) {
        updatedScenarios[index].name = tempScenarioName;
        this.setState({
          scenarios: updatedScenarios,
          currentScenario: updatedScenarios[index]
        }, () => {
          this.props.changeWhatIf(this.state.parameters, updatedScenarios, this.state.whatIfId);
          this.props.setWhatIfReducer(this.state.parameters, updatedScenarios, updatedScenarios[index]);
        });
        showNotificationWithIcon(i18n.t("Success"), i18n.t("Dashboard.Data.WhatIf.SuccessfullyUpdated"), "success");
      }
    } else {
      // Create new scenario
      const scenario = {
        id: uuid(),
        name: tempScenarioName,
        scenarioParametersList: parameters.map((param) => ({
          id: param.id,
          currentValue: param.currentValue,
        })),
      };

      this.addScenario(scenario);
      showNotificationWithIcon(
        i18n.t("Success"),
        i18n.t("Dashboard.Data.WhatIf.ScenarioSuccessfullyAdded"),
        "success"
      );
    }

    this.closeSaveScenarioModal();
  };

  /** Opens delete confirmation modal */
  openDeleteConfirmModal = () => {
    this.setState({ showDeleteConfirmModal: true });
  };

  /** Closes delete confirmation modal */
  closeDeleteConfirmModal = () => {
    this.setState({ showDeleteConfirmModal: false });
  };

  /** Handles actual scenario deletion */
  handleConfirmDelete = () => {
    if (this.state.currentScenario) {
      this.removeScenario(this.state.currentScenario.id);
      this.closeDeleteConfirmModal();
      showNotificationWithIcon(i18n.t("Success"), i18n.t("Dashboard.Data.WhatIf.ScenarioSuccessfullyDeleted"), "success");
    }
  };

  render() {
    let buttonTheme = this.props.theme?.buttons?.whatIfButton;
    let hasAnyParameter = this.state.parameters.length > 0 ? true : false;

    if (this.props.isScreenModeMobile && !hasAnyParameter) {
      return null;
    }

    return (
      <div className={"whatIf"}>
        <Modal
          id="dashboard-what-if-popup"
          open={this.state.visibility}
          placement="right-top"
          updatePositionDeps={[hasAnyParameter, this.props.isScreenModeMobile]}
          trigger={
            <div style={{ display: "inline-block" }}>
              <Button
                id="whatIfButton"
                className="dashboard-button circle-button plugin-button"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => this.whatIfOnClick(!this.state.visibility)}
                icon={getIcon(
                  buttonTheme?.icon,
                  <WhatIfIcon
                    className={"whatIf-icon"}
                    style={{ width: 30, height: 30, fill: "currentColor" }}
                  />,
                  { fontSize: 25, position: "relative" },
                )}
                shape="circle"
              >
                <style>
                  {buttonTheme &&
                    `
              body #whatIfButton {
                  background-color: ${buttonTheme?.backgroundColor};
                  color: ${buttonTheme?.textColor};
                  border-color: ${buttonTheme?.textColor}33;
              }

              body #whatIfButton:hover,
              body #whatIfButton:focus,
              body #whatIfButton:active {
                  background-color: ${buttonTheme?.backgroundColorActive};
                  color: ${buttonTheme?.textColorActive};
                  border-color: ${buttonTheme?.textColorActive}33;
              }
            `}
                </style>
              </Button>
            </div>
          }
          onCancel={() => this.whatIfOnClick(false)}
          width={520}
          bodyStyle={{ padding: 0 }}
          footer={
            this.state.activeMode === MODE_WHAT_IF &&
              this.props.readOnly === "WRITE" ? (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button type="primary" onClick={this.openSaveScenarioModal}>
                  {i18n.t("Dashboard.Data.WhatIf.SaveCurrentScenario")}
                </Button>
              </div>
            ) : null
          }
          closable={true}
          header={null}
        >
          <div className="what-if-compact-container">
            {this.props.readOnly === "WRITE" && !this.props.isScreenModeMobile && (
              <div className="what-if-compact-header">
                <Radio.Group
                  onChange={this.handleModeChange}
                  value={this.state.activeMode}
                  buttonStyle="solid"
                  className="what-if-segmented-control"
                >
                  <Radio.Button value={MODE_VARIABLES} data-testid="dashboard-variables-tab-button">
                    {i18n.t("DashboardVariables.Title")}
                  </Radio.Button>
                  <Radio.Button value={MODE_WHAT_IF} data-testid="what-if-tab-button">
                    {i18n.t("Dashboard.Data.WhatIf.Title")}
                  </Radio.Button>
                </Radio.Group>
              </div>
            )}

            <div className="what-if-compact-body">
              {this.state.activeMode === MODE_WHAT_IF && (
                <>
                  {this.props.readOnly === "WRITE" && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 8 }}>
                        <Select
                          style={{ flex: 1 }}
                          placeholder={i18n.t("Dashboard.Data.WhatIf.ScenarioName")}
                          allowClear
                          value={this.state.currentScenario?.id}
                          onChange={this.updateCurrentValuesByScenario}
                          getPopupContainer={getPopupContainer}
                        >
                          {this.state.scenarios.map(s => (
                            <Option key={s.id} value={s.id}>{s.name}</Option>
                          ))}
                        </Select>
                        {this.state.currentScenario && (
                          <>
                            <Tooltip title={i18n.t("Update")}>
                              <Button
                                icon={<SaveOutlined />}
                                size="small"
                                onClick={this.handleUpdateScenario}
                              />
                            </Tooltip>
                            <Tooltip title={i18n.t("Rename")}>
                              <Button
                                icon={<EditOutlined />}
                                size="small"
                                onClick={this.openEditScenarioModal}
                              />
                            </Tooltip>
                            <Tooltip title={i18n.t("Delete")}>
                              <Button
                                icon={<DeleteOutlined />}
                                size="small"
                                danger
                                onClick={this.openDeleteConfirmModal}
                              />
                            </Tooltip>
                          </>
                        )}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600, color: '#595959' }}>
                          {i18n.t("Dashboard.Data.WhatIf.Parameters")}
                        </span>
                        {this.state.parameters.length > 0 &&
                          <Button
                            type="primary"
                            size="small"
                            onClick={this.handleAddParameter}
                            icon={<PlusOutlined />}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "2px",
                            }}
                          >
                            {i18n.t("Add")}
                          </Button>}
                      </div>
                    </div>
                  )}

                  <List
                    readOnly={this.props.readOnly}
                    setSelectedParameters={this.setSelectedParameters}
                    removeParameter={this.removeParameter}
                    updateCurrentValue={this.updateCurrentValue}
                    parameters={this.state.parameters}
                    isScreenModeMobile={this.props.isScreenModeMobile}
                    onClickAddParameter={this.handleAddParameter}
                  />
                </>
              )}

              {this.state.activeMode === MODE_VARIABLES && (
                <DashboardVariableTable
                  key={JSON.stringify(this.props.dashboardVariables)}
                  variables={this.props.dashboardVariables}
                  activeValues={this.props.activeDashboardVariableValues}
                  onAdd={this.handleAddVariable}
                  onEdit={this.handleEditVariable}
                />
              )}
            </div>
          </div>
        </Modal>

        <Modal
          header={this.state.isEditingScenario ? i18n.t("Rename") : i18n.t("Dashboard.Data.WhatIf.SaveCurrentScenario")}
          open={this.state.showSaveScenarioModal}
          onCancel={this.closeSaveScenarioModal}
          onOk={this.handleSaveScenario}
          okText={i18n.t("Save")}
          cancelText={i18n.t("Cancel")}
          width={400}
          centered={true}
          closable={false}
        >
          <div style={{ padding: "20px 0" }}>
            <p style={{ marginBottom: 10 }}>{i18n.t("Dashboard.Data.WhatIf.ScenarioName")}</p>
            <Input
              autoFocus
              placeholder={i18n.t("Dashboard.Data.WhatIf.ScenarioName")}
              value={this.state.tempScenarioName}
              onChange={(e) => this.setState({ tempScenarioName: e.target.value })}
              onPressEnter={this.handleSaveScenario}
            />
          </div>
        </Modal>

        <Modal
          header={i18n.t("DeleteScenario")}
          open={this.state.showDeleteConfirmModal}
          onCancel={this.closeDeleteConfirmModal}
          onOk={this.handleConfirmDelete}
          okText={i18n.t("Yes")}
          cancelText={i18n.t("No")}
          width={400}
          centered={true}
          okButtonProps={{ danger: true }}
          closable={false}
        >
          <div style={{ padding: "20px 0", textAlign: "center" }}>
            <p>{i18n.t("Dashboard.Data.WhatIf.AreYouSureDeleteScenario")} {this.state.currentScenario?.name}?</p>
          </div>
        </Modal>

        <Modal
          header={i18n.t("Warning")}
          open={this.state.showVariableDeleteConfirmModal}
          onCancel={this.handleCloseVariableDeleteConfirm}
          onOk={this.handleConfirmVariableDelete}
          okText={i18n.t("Yes")}
          cancelText={i18n.t("No")}
          width={400}
          centered={true}
          okButtonProps={{ danger: true }}
          closable={false}
          destroyOnClose={true}
        >
          <div style={{ padding: "20px 0", textAlign: "center" }}>
            <p>{i18n.t("DashboardVariables.AreYouSureDeleteDashboardVariable")} {this.state.variableToDelete?.name}?</p>
          </div>
        </Modal>

        <Modal
          header={i18n.t("DashboardVariables.WarningRangeNotSupportedByPlugin")}
          open={this.state.showVariableDisconnectConfirmModal}
          onCancel={this.handleCancelVariableDisconnect}
          onOk={this.handleConfirmVariableDisconnect}
          okText={i18n.t("Yes")}
          cancelText={i18n.t("No")}
          width={400}
          centered={true}
          closable={false}
          destroyOnClose={true}
        >
          {i18n.t("DashboardVariables.AreYouSureDisconnectPlugin")}
        </Modal>

        <Modal
          header={this.state.selectedVariable ? i18n.t("DashboardVariables.Edit") : i18n.t("DashboardVariables.AddNew")}
          open={this.state.showVariableModal}
          onCancel={this.handleCancelVariableEdit}
          footer={null}
          width={500}
          centered={true}
          destroyOnClose={true}
        >
          <div style={{ padding: "20px 0" }}>
            <DashboardVariableCreateUpdate
              variables={this.props.dashboardVariables}
              variable={this.state.selectedVariable}
              onSave={this.handleSaveVariable}
              onCancel={this.handleCancelVariableEdit}
              onDelete={this.handleOpenVariableDeleteConfirm}
            />
          </div>
        </Modal>

        <Modal
          header={this.state.selectedParameter ? i18n.t("Dashboard.Data.WhatIf.Edit") : i18n.t("Dashboard.Data.WhatIf.AddNew")}
          open={this.state.showParameterModal}
          onCancel={this.handleCancelEdit}
          footer={null}
          width={500}
          centered={true}
        >
          <div>
            <CreateUpdate
              cancelEditParameter={this.handleCancelEdit}
              applyWhatIfParameter={this.applyWhatIfParameter}
              parameter={this.state.selectedParameter}
            />
          </div>
        </Modal>
      </div>
    );
  }
}
